import { VideoModel } from "@courses-pm2/api/src/ts/models";
import { Engine, resolveMediaPath } from "@courses-pm2/api/src/ts/utils";


import { parseContentElement } from "@courses-pm2/api/src/ts/parser/parseContentElement";
import { CallToActionCardsModel, CtaComponentCard } from "../../models/elements/CallToActionCardsModel";

/**
 * Helper that parses <Video /> elements in the response from the pm2 backend.
 */
export async function parseCallToActionCards({ node, portalId, engine }: {
    node: Element;
    portalId: string;
    engine?: Engine;
}): Promise<CallToActionCardsModel> {
    // Announce that a model has been identified if possible
    if (engine) {
        engine.announce(CallToActionCardsModel);
    }

    const cardsContainer = Array.from(node.childNodes).find((it): it is Element => {
        return (
            "tagName" in it &&
            typeof it.tagName === "string" &&
            it.tagName.toLowerCase() === "CallToActionCards".toLowerCase()
        );
    });

    const cards = Array.from(cardsContainer?.children ?? [])
        .map((it) => parseCtaCard(it))
        .filter((it): it is CtaComponentCard => it !== null);

    return new CallToActionCardsModel({
        engine,
        portalId,
        ...parseContentElement(node),
        title: node.getAttribute("title") ?? undefined,
        cards
    });
}

function parseCtaCard(node: Element): CtaComponentCard | null {
    const linkTarget = (() => {
      switch (node.getAttribute("linkTarget")) {
        case "LightBox":
          return "lightbox";

        case "Download":
          return "download";

        case "NewTab":
          return "new-tab";

        case "SameTab":
          return "same-tab";

        default:
          console.warn(
            "Unable to recognice LinkTarget for CtaComponentCard",
            node
          );
          return null;
      }
    })() satisfies CtaComponentCard["link"]["target"] | null;

    if (linkTarget === null) {
      return null;
    }

    const colorVariant = (() => {
      switch (node.getAttribute("color")) {
        case "bokstype-1":
          return "type-1";

        case "bokstype-2":
          return "type-2";

        case "bokstype-3":
          return "type-3";

        default:
          return undefined;
      }
    })() satisfies CtaComponentCard["meta"]["colorVariant"];

    const imageSrc = node.getAttribute("imageUrl");

    const href = (() => {
      switch (linkTarget) {
        case "download":
          return (
            resolveMediaPath(node.getAttribute(
              "downloadUrl"
            ) ?? "")
          );

        case "new-tab":
          return node.getAttribute("externalUrl") ?? "";

        case "lightbox":
            return `/learningObject/${node.getAttribute("linkId") ?? ""}`;

        case "same-tab":
        default:
          switch (node.getAttribute("linkTemplate")) {
            case "ResourcePage":
            case "Resource":
              return `${node.getAttribute(
                "linkShortPath"
              )}?resource=${node.getAttribute("linkId")}`;

            default:
              return node.getAttribute("linkShortPath") ?? "";
          }
      }
    })();

    return {
      title: node.getAttribute("title") ?? "",
      description: node.getAttribute("description") ?? undefined,

      imageSrc: resolveMediaPath(imageSrc),
      link: {
        id: node.getAttribute("linkId") ?? "",
        target: linkTarget,
        href,
      },

      meta: {
        colorVariant,
      },
    };
  }
